"use client"
import Image from "next/image"
import Link from "next/link"
import { Card, CardContent } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import {
    Calendar,
    ArrowLeft,
    Clock,
    Share2,
    Facebook,
    Twitter,
    Linkedin,
    Instagram
} from "lucide-react"

import AddReview from "@/components/pages/blogs/reviews/AddReview"
import { useState } from "react"
import "@/styles/quil.styles.css";

interface BlogPageProps {
    blog: Blog
    randomBlogs: Blog[]
    reviews: BlogReviews[]
}

export function BlogPage({ blog, randomBlogs, reviews }: BlogPageProps) {
    const [isShareModalOpen, setIsShareModalOpen] = useState(false)
    const getShareUrl = (platform: string) => {
        const postUrl = encodeURIComponent(`${window.location.origin}/blog-details/${blog.slug}`)
        const title = encodeURIComponent(blog.title)
        const description = encodeURIComponent(blog.content.replace(/<[^>]*>/g, "").substring(0, 200) + "...")
        const image = encodeURIComponent(blog.image || "")

        switch (platform) {
            case "facebook":
                return `https://www.facebook.com/sharer/sharer.php?u=${postUrl}&quote=${title}`
            case "twitter":
                return `https://twitter.com/intent/tweet?url=${postUrl}&text=${title}&hashtags=blog`
            case "linkedin":
                return `https://www.linkedin.com/sharing/share-offsite/?url=${postUrl}&title=${title}&summary=${description}`
            case "instagram":
                return `https://www.instagram.com/`
            case "share":
                return `https://skillsewa.info/`
            default:
                return "#"
        }
    }

    const handleSocialShare = (platform: string, event: React.MouseEvent) => {
        event.stopPropagation()

        if (platform === "instagram" || platform === "share") {
            navigator.clipboard.writeText(`${window.location.origin}/blog-details/${blog.slug}`)
            alert("Link copied to clipboard! You can now share it on any platform.")
            return
        }

        const shareUrl = getShareUrl(platform)
        window.open(shareUrl, "_blank", "width=600,height=400,scrollbars=yes,resizable=yes")
    }
    return (
        <div className="min-h-screen bg-background">
            {/* Header with breadcrumb */}
            <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
                    <div className="flex h-16 items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <Link
                                href="/blogs"
                                className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                <span className="text-sm font-medium">Blogs</span>
                            </Link>
                            <span className="text-muted-foreground">/</span>
                            <span className="text-sm font-medium text-foreground">Current Article</span>
                        </div>
                        <Badge variant="secondary" className="hidden sm:flex items-center space-x-1">
                            <Calendar className="h-3 w-3" />
                            <span>{new Date(blog.created_at).toLocaleDateString()}</span>
                        </Badge>
                    </div>
                </div>
            </header>

            <main className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 xl:gap-12">
                    {/* Main Content */}
                    <article className="lg:col-span-2 space-y-8">
                        {/* Article Header */}
                        <div className="space-y-6">
                            <div className="space-y-4">
                                <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight">{blog.title}</h1>
                                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                                    <div className="flex items-center space-x-1">
                                        <Calendar className="h-4 w-4" />
                                        <time>{new Date(blog.created_at).toLocaleDateString()}</time>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                        <Clock className="h-4 w-4" />
                                        <span>5 min read</span>
                                    </div>
                                </div>
                            </div>

                            {/* Featured Image with hover overlay */}
                            <div
                                className="relative aspect-video w-full overflow-hidden rounded-lg group cursor-pointer"
                                onClick={() => setIsShareModalOpen(true)}
                            >
                                <Image
                                    src={blog.image || "/placeholder.svg"}
                                    alt={blog.image_alt}
                                    fill
                                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                                    priority
                                />
                                {/* Hover overlay with social icons */}
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                    <div className="flex items-center space-x-4">
                                        <div className="p-3 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
                                            onClick={(e) => handleSocialShare("share", e)}
                                        >
                                            <Share2 className="h-6 w-6 text-white" />
                                        </div>
                                        <div
                                            className="p-3 bg-blue-600/80 backdrop-blur-sm rounded-full hover:bg-blue-600 transition-colors cursor-pointer"
                                            onClick={(e) => handleSocialShare("facebook", e)}
                                        >
                                            <Facebook className="h-6 w-6 text-white" />
                                        </div>
                                        <div
                                            className="p-3 bg-sky-500/80 backdrop-blur-sm rounded-full hover:bg-sky-500 transition-colors cursor-pointer"
                                            onClick={(e) => handleSocialShare("twitter", e)}
                                        >
                                            <Twitter className="h-6 w-6 text-white" />
                                        </div>
                                        <div
                                            className="p-3 bg-blue-700/80 backdrop-blur-sm rounded-full hover:bg-blue-700 transition-colors cursor-pointer"
                                            onClick={(e) => handleSocialShare("linkedin", e)}
                                        >
                                            <Linkedin className="h-6 w-6 text-white" />
                                        </div>
                                        <div
                                            className="p-3 bg-pink-600/80 backdrop-blur-sm rounded-full hover:bg-pink-600 transition-colors cursor-pointer"
                                            onClick={(e) => handleSocialShare("instagram", e)}
                                        >
                                            <Instagram className="h-6 w-6 text-white" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <Separator />

                        {/* Article Content */}
                        <div
                            className="editor border rounded-xl prose prose-gray dark:prose-invert max-w-none prose-lg p-5"
                            dangerouslySetInnerHTML={{ __html: blog.content }}
                        />

                        <AddReview blogId={blog.id} slug={blog.slug} />
                        {/* Reviews Section */}
                        <div className="space-y-6 pt-8">
                            <Separator />
                            <div className="space-y-4">
                                <h3 className="text-xl font-semibold">Comments ({reviews.length})</h3>
                                <div className="space-y-4">
                                    {reviews.map((review) => (
                                        <Card key={review._id}>
                                            <CardContent className="p-4">
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-medium">{review.author}</span>
                                                        <time className="text-sm text-muted-foreground">
                                                            {new Date(review.createdAt).toLocaleDateString()}
                                                        </time>
                                                    </div>
                                                    <p className="text-muted-foreground">{review.comment}</p>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </article>

                    {/* Sidebar - Recent Posts */}
                    <aside className="space-y-6">
                        <div className="sticky top-24 space-y-6">
                            <div className="space-y-4">
                                <div className="border-b border-gray-200 pb-2">
                                    <h2 className="text-lg font-semibold text-gray-900 uppercase tracking-wide">Recent Posts</h2>
                                    <div className="w-8 h-0.5 bg-teal-500 mt-1"></div>
                                </div>
                                <div className="space-y-3">
                                    {randomBlogs.map((recentBlog) => (
                                        <Link key={recentBlog.id} href={`/blog-details/${recentBlog.slug}`} className="block group">
                                            <div className="flex items-start space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                                                <div className="flex-shrink-0 w-16 h-12 relative overflow-hidden rounded">
                                                    <Image
                                                        src={recentBlog.image || "/placeholder.svg"}
                                                        alt={`The image is about: ${recentBlog.image_alt}`}
                                                        fill
                                                        className="object-cover"
                                                    />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-teal-600 transition-colors">
                                                        {recentBlog.title}
                                                    </h3>
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>

                                <div className="pt-6">
                                    <div className="border-b border-gray-200 pb-2">
                                        <h2 className="text-lg font-semibold text-gray-900 uppercase tracking-wide">Archives</h2>
                                        <div className="w-8 h-0.5 bg-teal-500 mt-1"></div>
                                    </div>
                                    <div className="pt-4">
                                        <Link
                                            href="/blogs"
                                            className="text-sm text-gray-600 hover:text-teal-600 transition-colors uppercase tracking-wide"
                                        >
                                            Last Six Months
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </main>
        </div>
    )
}
